/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import IntroSection from '../../common/IntroSection';
import MainSection from '../components/MainSection';
import NonFieldErrorBanner from '../components/NonFieldErrorBanner';
import PromoteSiteNewsletters from '../components/PromoteSiteNewsletters';
import PriceSummaryBox from '../components/PriceSummaryBox';
import PromoteService from '../services/promoteService';
import { checkoutURL } from '../helpers/divewireConstants';
import PaymentIntentService from '../services/paymentIntentService';
import LoadingScreen from '../../../common/LoadingScreen';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';
import PromoteSiteList from '../components/PromoteSiteList';
import EventsHelper from '../helpers/eventsHelper';
import DiveMessageBanner from '../../common/DiveMessageBanner';
import { scrollToElement } from '../../../common/helpers/scrollTo';
import PRESS_RELEASE_STATUS from '../helpers/pressReleaseUtils';

const PromoteScreen = () => {
  // eslint-disable-next-line no-unused-vars,max-len,object-curly-newline
  const { pressRelease, pressReleaseMetadata, promoteSites, dispatchSites, nonFieldError, setNonFieldError } = useOutletContext();
  const navigate = useNavigate();
  const { token } = useParams();

  if (pressRelease?.status === PRESS_RELEASE_STATUS.PAID) {
    window.location.replace(`${checkoutURL}/${token}/create`);
    return null;
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCheckoutCodeInput, setShowCheckoutCodeInput] = useState(false);
  // Instantiate an EventsHelper and store in a ref so that EventsHelper.pressRelease
  // data remains constant for synchronous calculations.
  const eventsHelperRef = useRef(
    new EventsHelper(
      pressRelease,
      pressReleaseMetadata,
      promoteSites.sitesList,
    ),
  );
  const numSitesRef = useRef(0);
  const numTopicNlsRef = useRef(0);

  // Track if cross-post section is in view
  const [crossPostInView, setCrossPostInView] = useState(true);

  // Set up IntersectionObserver to observe cross-post section visibility
  useEffect(() => {
    const crossPostSection = document.getElementById('cross-post-section');
    if (!crossPostSection) {
      return () => {};
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setCrossPostInView(entry.intersectionRatio >= 0.1);
      });
    }, {
      threshold: 0.1,
      rootMargin: '-310px 0px 0px 0px', // Account for sticky header height
    });
    observer.observe(crossPostSection);
    return () => observer.disconnect();
  }, []);

  const chosenSitesWithTopicNls = promoteSites.sitesList.filter((site) => (
    site.selected && site.topic_newsletter_add_on_options.length
  ));
  const promoteApi = new PromoteService(token);
  const paymentIntentApi = new PaymentIntentService(token);
  const newsletterAddOnPriceInDollars = pressReleaseMetadata.topic_newsletter_add_on_price / 100;

  /* eslint-disable camelcase */
  const validateSiteList = (scopeSiteList, prCrosspostSites, prAddOns) => {
    const primary_sites = scopeSiteList.filter((site) => site.primary);
    // Validate that only one site is designated as the primary site and that it is selected
    if (primary_sites.length !== 1 || !primary_sites[0].selected) {
      return false;
    }

    // Validate that no add_ons are selected if their corresponding site is not selected
    /* eslint-disable comma-dangle */
    const hasInvalidAddOns = scopeSiteList.some(
      (site) => !site.selected
          && site.topic_newsletter_add_on_options.some((addOn) => addOn.selected)
    );
    /* eslint-enable comma-dangle */
    if (hasInvalidAddOns) return false;

    // Validate that crosspost_sites matches the expected value based on siteList
    const selectedSiteIds = scopeSiteList
      .filter((site) => site.selected)
      .map((site) => site.site_id);
    if (!selectedSiteIds.every((id) => prCrosspostSites.includes(id))
        || !prCrosspostSites.every((id) => selectedSiteIds.includes(id))) {
      return false;
    }

    // Validate that topic_newsletter_add_ons matches the expected value based on siteList
    const selectedAddOnIds = scopeSiteList
      .flatMap((site) => site.topic_newsletter_add_on_options.filter((addOn) => addOn.selected))
      .map((addOn) => addOn.newsletter_schedule_id);
    if (!selectedAddOnIds.every((id) => prAddOns.includes(id))
        || !prAddOns.every((id) => selectedAddOnIds.includes(id))) {
      return false;
    }

    return true;
  };

  const triggerCartEvent = (type, productType, objectIds) => {
    // Use our live count of added sites and topic newsletters
    // in our individual product and total price calculations.
    const synchronouslyAddedItemCounts = {
      numIncomingSites: numSitesRef.current,
      numIncomingNlAddOns: numTopicNlsRef.current,
    };

    if (type === 'add') {
      if (objectIds.length > 1) {
        eventsHelperRef.current.triggerAddMultipleToCartEvent(
          objectIds,
          productType,
          synchronouslyAddedItemCounts,
        );
      } else {
        eventsHelperRef.current.triggerAddToCartEvent(
          objectIds[0],
          productType,
          synchronouslyAddedItemCounts,
        );
      }
    } else if (type === 'remove') {
      eventsHelperRef.current.triggerRemoveFromCartEvent(
        objectIds[0],
        productType,
        synchronouslyAddedItemCounts,
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isValid = validateSiteList(
      promoteSites.sitesList,
      pressRelease.crosspost_sites,
      pressRelease.topic_newsletter_add_ons,
    );
    if (isValid) {
      let promoteResponse;
      let paymentIntentResponse;
      try {
        promoteResponse = await promoteApi.updateSitesList(
          pressRelease.crosspost_sites,
          pressRelease.topic_newsletter_add_ons,
        );
        dispatchSites({
          type: 'SET_SITES',
          payload: promoteResponse,
        });
        paymentIntentResponse = await paymentIntentApi.updatePaymentIntent();
      } catch (apiError) {
        setIsSubmitting(false);
        displayBackendErrors(apiError, null, setNonFieldError, null);
      }
      if (promoteResponse && paymentIntentResponse) {
        setIsSubmitting(false);
        setNonFieldError('');
        navigate(`${checkoutURL}/${token}/payment`);
      }
    } else {
      console.log('Form invalid'); // TODO - Actual error handling
    }
    setIsSubmitting(false);
  };

  const handleAddSite = (siteId) => {
    dispatchSites({
      type: 'ADD_SELECTED_SITE',
      payload: siteId,
    });
    numSitesRef.current += 1;
    triggerCartEvent('add', 'publication', [siteId]);
  };

  const handleAddMultipleSites = (siteIds) => {
    const alreadyAddedSites = promoteSites.sitesList
      .filter((site) => site.selected === true)
      .map((site) => site.site_id);
    const siteIdsToAdd = siteIds.filter((item) => !alreadyAddedSites.includes(item));
    siteIdsToAdd.map((siteId) => (
      dispatchSites({
        type: 'ADD_SELECTED_SITE',
        payload: siteId,
      })
    ));
    numSitesRef.current += siteIdsToAdd.length;
    triggerCartEvent('add', 'publication', siteIdsToAdd);
  };

  const handleRemoveSite = (siteId) => {
    dispatchSites({
      type: 'REMOVE_SELECTED_SITE',
      payload: siteId,
    });
    numSitesRef.current -= 1;
    triggerCartEvent('remove', 'publication', [siteId]);
  };

  const handleAddAddon = (siteId, newsletterScheduleId) => {
    dispatchSites({
      type: 'ADD_ADDON',
      payload: {
        siteId,
        newsletterScheduleId,
      },
    });
    numTopicNlsRef.current += 1;
    triggerCartEvent('add', 'newsletter', [newsletterScheduleId]);
  };

  const handleRemoveAddon = (siteId, newsletterScheduleId) => {
    dispatchSites({
      type: 'REMOVE_ADDON',
      payload: {
        siteId,
        newsletterScheduleId,
      },
    });
    numTopicNlsRef.current -= 1;
    triggerCartEvent('remove', 'newsletter', [newsletterScheduleId]);
  };

  return (
    <>
      <main id="skip-link-target">
        <div className="row intro-section-wrapper">
          <div className="columns">
            <IntroSection title="Boost Visibility">
              Promote your announcements across any of Industry Dive's{' '}
              30+ publications homepage and newsletters.
            </IntroSection>
          </div>
        </div>
        <div className="row">
          <div className="large-7 columns">
            <MainSection id="cross-post-section" className="press-release-content" title="Publish to additional publications">
              <div className="explanation-text" style={{ marginTop: '-12px' }}>
                Expand your press release visibility in front of our daily
                newsletter subscribers and take advantage of volume-based discounts.
              </div>
              {promoteSites.sitesList.length > 0 ? (
                <PromoteSiteList
                  sitesList={promoteSites.sitesList}
                  currentSiteId={pressReleaseMetadata.current_site_pk}
                  handleAddSite={handleAddSite}
                  handleAddMultipleSites={handleAddMultipleSites}
                  handleRemoveSite={handleRemoveSite}
                />
              ) : (
                <p>No sites available.</p>
              )}
            </MainSection>

            <MainSection id="topic-newsletter-section" cslassName="press-release-content" title="Customize a targeted audience">
              <div className="explanation-text" style={{ marginTop: '-12px' }}>
                For an additional ${newsletterAddOnPriceInDollars} each, you can share your headline in the
                "Company Announcements" section of our topic-focused newsletters.
              </div>

              <div className="promote-items-list">
                {chosenSitesWithTopicNls.length > 0 ? (chosenSitesWithTopicNls
                  .map((site) => (
                    <PromoteSiteNewsletters
                      key={site.site_id}
                      site={site}
                      newsletters={site.topic_newsletter_add_on_options}
                      handleNewsletterSelect={handleAddAddon}
                      handleNewsletterDeselect={handleRemoveAddon}
                    />
                  ))
                ) : (
                  <DiveMessageBanner messageType="info" additionalClasses="topic-newsletter-message" isClosable={false}>
                    No additonal topic-focused newsletters with&nbsp;
                    {promoteSites.sitesList.find((site) => site.primary).site_name}
                  </DiveMessageBanner>
                )}
              </div>
            </MainSection>
          </div>
          <div className="large-5 columns price-summary-section">
            <PriceSummaryBox
              pressRelease={pressRelease}
              pressReleaseMetadata={pressReleaseMetadata}
              sitesList={promoteSites.sitesList}
              showCheckoutCodeInput={showCheckoutCodeInput}
              onToggleCheckoutCodeInput={() => setShowCheckoutCodeInput(!showCheckoutCodeInput)}
            />
            <section className="promote-actions button-container">
              {nonFieldError && (
                <NonFieldErrorBanner
                  setErrorCallback={setNonFieldError}
                  prContactEmail={pressReleaseMetadata.pressrelease_contact_email}
                />
              )}
              <button
                id="next-step-button"
                type="button"
                className="button button--soft"
                onClick={() => {
                  if (!crossPostInView) {
                    scrollToElement(document.getElementById('cross-post-section'), 300);
                  } else {
                    scrollToElement(document.getElementById('topic-newsletter-section'), 300);
                  }
                }}
              >
                {!crossPostInView ? 'Return' : 'Next'}
              </button>
              {!crossPostInView && (
                <button
                  id="form-submit-pr"
                  type="submit"
                  className="button button--medium"
                  onClick={handleSubmit}
                >
                  Continue to checkout
                </button>
              )}
            </section>
          </div>
        </div>
      </main>
      {isSubmitting && <LoadingScreen isSubmitting={isSubmitting} />}
    </>
  );
};

export default PromoteScreen;
